import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  FaEnvelope,
  FaLock,
  FaSave,
  FaSignOutAlt,
  FaUser,
  FaUserCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PROFILE_THEME = {
  shell: "bg-gradient-to-br from-[#180303] via-[#2a0707] to-[#0f0202]",
  frame: "shadow-[0_0_90px_rgba(255,59,59,0.18)]",
  accentSoft: "bg-red-500/20",
  accentBorder: "border-red-300/30",
  panel: "bg-[#12091f]/72",
  panelStrong: "bg-[#0f0c18]/88",
  pill: "bg-white/10",
};

export default function Profile({ session }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAuth, setSavingAuth] = useState(false);
  const [message, setMessage] = useState("");
  const [errorText, setErrorText] = useState("");

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState(session?.user?.email || "");
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    document.title = "Profile • Light Idea Productions";
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      if (!session?.user?.id) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("username, email")
        .eq("id", session.user.id)
        .single();

      if (!mounted) return;

      if (error) {
        setErrorText(error.message || "Failed to load profile.");
        setLoading(false);
        return;
      }

      setUsername(data?.username || "");
      setEmail(data?.email || session.user.email || "");
      setLoading(false);
    };

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [session]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setMessage("");
    setErrorText("");

    if (!username.trim()) {
      setSavingProfile(false);
      setErrorText("Username is required.");
      return;
    }

    const { error } = await supabase
      .from("profiles")
      .update({
        username: username.trim(),
        email: email.trim(),
      })
      .eq("id", session.user.id);

    setSavingProfile(false);

    if (error) {
      setErrorText(error.message || "Failed to update profile.");
      return;
    }

    setMessage("Profile details updated.");
  };

  const handleAuthUpdate = async (e) => {
    e.preventDefault();
    setSavingAuth(true);
    setMessage("");
    setErrorText("");

    const updates = {};

    if (email.trim() && email.trim() !== session?.user?.email) {
      updates.email = email.trim();
    }

    if (newPassword.trim()) {
      updates.password = newPassword;
    }

    if (!updates.email && !updates.password) {
      setSavingAuth(false);
      setErrorText("Nothing to update.");
      return;
    }

    const { error } = await supabase.auth.updateUser(updates);

    setSavingAuth(false);

    if (error) {
      setErrorText(error.message || "Failed to update account.");
      return;
    }

    if (updates.email) {
      await supabase
        .from("profiles")
        .update({ email: email.trim() })
        .eq("id", session.user.id);
    }

    setNewPassword("");
    setMessage(
      updates.email
        ? "Account updated. If email confirmation is enabled, confirm your new email address."
        : "Password updated successfully.",
    );
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0f0202] text-white">
        Loading profile...
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${PROFILE_THEME.shell} text-white`}>
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.16),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(255,59,59,0.14),transparent_30%)]" />
      </div>

      <div className="relative mx-auto w-full max-w-[1800px] px-3 py-6 sm:px-6 sm:py-8 lg:px-8">
        <section className="mb-8">
          <div
            className={`overflow-hidden rounded-[24px] border border-white/10 ${PROFILE_THEME.panelStrong} ${PROFILE_THEME.frame}`}
          >
            <div className="relative min-h-[260px]">
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-black/25" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.20),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(255,59,59,0.20),transparent_30%)]" />

              <div className="relative z-10 flex min-h-[260px] items-end">
                <div className="max-w-3xl px-4 pb-6 sm:px-8 sm:pb-8 lg:px-10 lg:pb-10">
                  <div
                    className={`mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-white/90 ${PROFILE_THEME.pill}`}
                  >
                    <FaUserCircle />
                    <span>Account</span>
                  </div>

                  <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
                    {username || "Your Profile"}
                  </h1>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 sm:text-base">
                    Manage your username, email, password, and sign out of your
                    account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {message ? (
          <div
            className={`mb-5 rounded-2xl border px-4 py-3 text-sm text-white/90 ${PROFILE_THEME.accentBorder} ${PROFILE_THEME.accentSoft}`}
          >
            {message}
          </div>
        ) : null}

        {errorText ? (
          <div className="mb-5 rounded-2xl border border-red-400/30 bg-red-500/15 px-4 py-3 text-sm text-white/90">
            {errorText}
          </div>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-2">
          <section
            className={`rounded-[24px] border border-white/10 ${PROFILE_THEME.panel} p-5 backdrop-blur-xl sm:p-6`}
          >
            <div className="mb-5">
              <div className="mb-1 text-[11px] uppercase tracking-[0.28em] text-white/45">
                Public profile
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">
                Update details
              </h2>
            </div>

            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <Field
                icon={<FaUser />}
                label="Username"
                type="text"
                value={username}
                onChange={setUsername}
                placeholder="Your username"
                autoComplete="username"
              />

              <Field
                icon={<FaEnvelope />}
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                autoComplete="email"
              />

              <button
                type="submit"
                disabled={savingProfile}
                className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-5 py-3.5 text-sm font-semibold text-black transition hover:scale-[1.01] hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                <span>{savingProfile ? "Saving..." : "Save profile"}</span>
              </button>
            </form>
          </section>

          <section
            className={`rounded-[24px] border border-white/10 ${PROFILE_THEME.panel} p-5 backdrop-blur-xl sm:p-6`}
          >
            <div className="mb-5">
              <div className="mb-1 text-[11px] uppercase tracking-[0.28em] text-white/45">
                Security
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">
                Email & password
              </h2>
            </div>

            <form onSubmit={handleAuthUpdate} className="space-y-4">
              <Field
                icon={<FaEnvelope />}
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                autoComplete="email"
              />

              <Field
                icon={<FaLock />}
                label="New password"
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="Enter a new password"
                autoComplete="new-password"
              />

              <button
                type="submit"
                disabled={savingAuth}
                className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-5 py-3.5 text-sm font-semibold text-black transition hover:scale-[1.01] hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                <span>{savingAuth ? "Updating..." : "Update account"}</span>
              </button>
            </form>

            <button
              type="button"
              onClick={handleLogout}
              className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white/10 px-5 py-3.5 text-sm font-medium text-white backdrop-blur-md transition hover:bg-white/15"
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({
  icon,
  label,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
}) {
  return (
    <label className="block">
      <div className="mb-2 text-sm font-medium text-white/88">{label}</div>
      <div className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-md transition focus-within:border-white/20 focus-within:bg-white/[0.07]">
        <div className="shrink-0 text-white/55">{icon}</div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/35"
        />
      </div>
    </label>
  );
}
